/**
 * Job status taxonomy — the 8/11-stage Altitude pipeline (migration 0033) plus
 * the catalog's status→color bands (LEGACY-CATALOG.md §1.3). Shared so the web
 * grids, the phone app's status picker, and any analytics agree on the same
 * stage list, ordering, buckets, and colors.
 */

export type StatusBucket = 'open' | 'pending' | 'active' | 'onhold' | 'done' | 'lost';

export interface JobStage {
  key: string;
  label: string;
  bucket: StatusBucket;
  isTerminal: boolean;
  sortOrder: number;
}

/** Seeded in migration 0033. Use as the canonical fallback when the live
 *  `job_statuses` table hasn't been fetched. */
export const ALTITUDE_STAGES: JobStage[] = [
  { key: 'open',                   label: 'Open',                     bucket: 'open',    isTerminal: false, sortOrder: 10 },
  { key: 'pending_sales',          label: 'Pending Sales',            bucket: 'pending', isTerminal: false, sortOrder: 20 },
  { key: 'pre_production',         label: 'Pre-Production',           bucket: 'active',  isTerminal: false, sortOrder: 30 },
  { key: 'work_in_progress',       label: 'Work In Progress',         bucket: 'active',  isTerminal: false, sortOrder: 40 },
  { key: 'completed_no_paperwork', label: 'Completed – No Paperwork', bucket: 'active',  isTerminal: false, sortOrder: 50 },
  { key: 'invoice_pending',        label: 'Invoice Pending',          bucket: 'pending', isTerminal: false, sortOrder: 60 },
  { key: 'accounts_receivable',    label: 'Accounts Receivable',      bucket: 'pending', isTerminal: false, sortOrder: 70 },
  { key: 'waiting_to_close',       label: 'Waiting to Close',         bucket: 'pending', isTerminal: false, sortOrder: 80 },
  { key: 'closed',                 label: 'Closed',                   bucket: 'done',    isTerminal: true,  sortOrder: 90 },
  { key: 'on_hold',                label: 'On Hold',                  bucket: 'onhold',  isTerminal: false, sortOrder: 100 },
  { key: 'lost',                   label: 'Lost / Cancelled',         bucket: 'lost',    isTerminal: true,  sortOrder: 110 },
];

const STAGE_BY_KEY = new Map(ALTITUDE_STAGES.map((s) => [s.key, s]));

export function stageLabel(statusKey: string | null | undefined): string {
  if (!statusKey) return '—';
  return STAGE_BY_KEY.get(statusKey)?.label ?? statusKey;
}

/** Hex color for a status, matching the catalog's tonal conventions (§1.3,
 *  §1.6). Works off either a status_key or a free-text status label. */
export function statusColor(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (!s) return '#6b7280';                                  // slate (unknown)
  if (s.includes('complete')) return '#16a34a';              // green
  if (s.includes('approv') || (s.includes('signed') && !s.includes('pending'))) return '#16a34a';
  if (s.includes('lost') || s.includes('cancel')) return '#dc2626'; // red
  if (s.includes('hold')) return '#f97316';                  // orange
  if (s.includes('pending')) return '#eab308';               // amber
  const stage = STAGE_BY_KEY.get(s);
  if (stage) {
    switch (stage.bucket) {
      case 'done': return '#16a34a';
      case 'lost': return '#dc2626';
      case 'onhold': return '#f97316';
      case 'pending': return '#eab308';
      case 'active': return '#2563eb';                       // blue
      default: return '#032e5b';                             // navy
    }
  }
  return '#032e5b';
}
